/**
 * T143 — /expenses list page (US1 MVP).
 * T489 — Filter-bar with URL-state sync (Quartal / Status / Kategorie /
 *        Lieferant / Quelle / Datum-Range).
 *
 * Filter state is the URL's search-params (validateSearch + useSearch +
 * useNavigate). Persistent across reloads, shareable, browser-back works.
 */
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, EmptyState, GlassPanel, LoadingSpinner, Modal, PageHeader, Pagination, SearchInput, StatusBadge, type StatusTone } from '../components/ui';
import { formatDateBerlin } from '../lib/format-date';
import { OcrConfidenceBadge } from '../components/ocr/OcrConfidenceBadge';
import { useOcrEngineState } from '../hooks/useOcrEngineState';
import { usePageSizePref } from '../hooks/usePageSizePref';
import { PageSizePicker } from '../components/shared/PageSizePicker';
import { DateBasisControls } from '../components/shared/DateBasisControls';
import { PeriodPicker } from '../components/shared/PeriodPicker';
import { useExpenses, useMergeExpenses } from '../services/api/expenses';
import { useDurcharbeiteQueue } from '../services/api/durcharbeite';
import { useCategories } from '../services/api/categories';
import { useSuppliers } from '../services/api/suppliers';
import { QuartalStatusBanner } from '../components/quartal/QuartalStatusBanner';
import { ImapStatusBanner } from '../components/expenses/ImapStatusBanner';
import { useToastHelpers } from '../components/ui/Toast';
import { useClosedQuartalsSet } from '../hooks/useClosedQuartalsSet';
import { getExpenseRowStatus } from '@armard/shared';
import {
  ExpenseRowStatusBadge,
  ROW_TINT_CLASSES,
} from '../components/expenses/ExpenseRowStatusBadge';
import { ExpenseStatusLegendTrigger } from '../components/expenses/ExpenseStatusLegend';

// T489: status / source values mirrored from shared/validation so the
// validateSearch schema below stays self-contained (avoids dragging the
// full shared zod surface into the route bundle).
const STATUS_VALUES = [
  'draft',
  'neu',
  'geprüft',
  'rückfrage_offen',
  'nicht_relevant',
  'zurückgezogen',
  'archiviert',
] as const;
const SOURCE_VALUES = ['manual', 'email', 'drive', 'erechnung', 'ki'] as const;

// Sprint 006 A2 — Entity-Status → Tone (ratifizierte Role-Map).
const STATUS_TONES: Record<string, StatusTone> = {
  draft: 'neutral',
  neu: 'info',
  geprüft: 'success',
  rückfrage_offen: 'warning',
  nicht_relevant: 'muted',
  zurückgezogen: 'muted',
  archiviert: 'muted',
};
type ExpenseStatusLiteral = (typeof STATUS_VALUES)[number];
type ExpenseSourceLiteral = (typeof SOURCE_VALUES)[number];

const expensesSearchSchema = z.object({
  dateBasis: z.enum(['invoiceDate', 'paymentDate']).optional().default('invoiceDate'),
  paymentStatus: z.enum(['all', 'known', 'unknown']).optional().default('all'),
  page: z.coerce.number().int().positive().optional().default(1),
  q: z.string().optional(),
  // Multi-selects accept a single value (from one URL key) or an array
  // (?status=neu&status=geprüft) — TanStack Router collapses repeated keys
  // into arrays for us; the optional() lets the absence-of-key still parse.
  status: z
    .union([z.enum(STATUS_VALUES), z.array(z.enum(STATUS_VALUES))])
    .optional()
    .transform((v) => (Array.isArray(v) ? v : v ? [v] : undefined)),
  categoryId: z
    .union([z.string().uuid(), z.array(z.string().uuid())])
    .optional()
    .transform((v) => (Array.isArray(v) ? v : v ? [v] : undefined)),
  supplierId: z.string().uuid().optional(),
  source: z
    .union([z.enum(SOURCE_VALUES), z.array(z.enum(SOURCE_VALUES))])
    .optional()
    .transform((v) => (Array.isArray(v) ? v : v ? [v] : undefined)),
  quartal: z
    .string()
    .regex(/^\d{4}-Q[1-4]$/)
    .optional(),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  // T527 — "Nur meine Uploads" quick-toggle. URL-state stays
  // boolean-flat; server applies `WHERE created_by_user_id = session.user`.
  mine: z.coerce.boolean().optional(),
  // T573 — page-size preference. URL wins over the localStorage
  // cursor so shared links with `?limit=200` are sticky.
  limit: z.coerce.number().int().positive().optional(),
});

type ExpensesSearch = z.infer<typeof expensesSearchSchema>;

export const Route = createFileRoute('/expenses')({
  component: ExpensesPage,
  validateSearch: expensesSearchSchema,
});

function ExpensesPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <ExpensesInner />
      </AppShell>
    </ProtectedRoute>
  );
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  neu: 'Neu',
  geprüft: 'Geprüft',
  rückfrage_offen: 'Rückfrage',
  nicht_relevant: 'Nicht relevant',
  zurückgezogen: 'Zurückgezogen',
  archiviert: 'Archiviert',
};

// T488/T493: source badge for the Quelle column. Compact icon + short label;
// the full provenance section lives on the detail page.
const SOURCE_LABELS: Record<string, { icon: string; label: string }> = {
  manual: { icon: '✋', label: 'Manuell' },
  email: { icon: '📧', label: 'E-Mail' },
  drive: { icon: '💾', label: 'Drive' },
  erechnung: { icon: '📄', label: 'eRechnung' },
  ki: { icon: '🤖', label: 'KI' },
};

// T488: re-export so the route module is the canonical surface, but the
// underlying constant lives in `./-expenses.columns` so the snapshot test can
// import it without running the route's `createFileRoute` side-effect.
// The `-` prefix excludes the helper + its test from TanStack Router's
// auto-route-tree scan.
export { EXPENSES_LIST_COLUMNS } from './-expenses.columns';

function formatMoney(amount: string, currency = 'EUR'): string {
  const num = Number(amount);
  return num.toLocaleString('de-DE', { style: 'currency', currency });
}

// T487: marker for rows whose invoice_date may still be the upload-fallback
// rather than an extracted/typed real Belegdatum. expense-upload.ts seeds
// invoice_date with today's date for every multi-upload draft; the
// apply-extracted pipeline (T481) later overwrites it from XML/OCR and
// promotes trustLevel to 'validated' (eRechnung) or 'review' (PDF-text/OCR).
//
// Only flag the 'review' tier — that's the tier where extraction definitely
// ran but the date is the weakest signal. Manual ('manual') stays ambiguous
// (could be a legit /new entry typed for today, no way to disambiguate
// without a schema-level `invoice_date_inferred` column). Validated rows
// are trusted regardless.
function isInferredUploadDate(e: { invoiceDate: string | null; createdAt: string; trustLevel: string }): boolean {
  if (e.invoiceDate === null) return false;
  if (e.trustLevel !== 'review') return false;
  return e.invoiceDate === e.createdAt.slice(0, 10);
}

function detectDuplicatesInList(
  items: ReadonlyArray<{
    id: string;
    supplierId: string | null;
    supplierInvoiceNumber: string | null;
    invoiceDate: string | null;
    grossTotal: string;
  }>
): Set<string> {
  // FR-013 4-key duplicate detection, batched client-side over the currently
  // loaded page. Flags every id that shares (supplier, invoiceNumber, date,
  // gross) with at least one other row in the same list. Rows with a
  // NULL invoiceDate cannot participate (the 4-key needs the date).
  const bucket = new Map<string, string[]>();
  for (const e of items) {
    if (!e.supplierId || !e.supplierInvoiceNumber || !e.invoiceDate) continue;
    const key = `${e.supplierId}|${e.supplierInvoiceNumber}|${e.invoiceDate}|${e.grossTotal}`;
    const list = bucket.get(key) ?? [];
    list.push(e.id);
    bucket.set(key, list);
  }
  const flagged = new Set<string>();
  for (const ids of bucket.values()) {
    if (ids.length > 1) {
      for (const id of ids) flagged.add(id);
    }
  }
  return flagged;
}

// T489: Sticky filter bar. Reads/writes URL search state via TanStack Router.
function ExpensesFilterBar(props: {
  search: ExpensesSearch;
  onChange: (patch: Partial<ExpensesSearch>) => void;
  categories: Array<{ id: string; name: string }>;
  suppliers: Array<{ id: string; displayName: string }>;
}) {
  const { search, onChange, categories, suppliers } = props;
  const [mobileExpanded, setMobileExpanded] = useState(false);
  // T565 (FR-UI A1.1): cap dropdown width at 200px in collapsed state so
  // the filter-bar stops over-extending when categories/suppliers have
  // long names. Browser-default <select> widens automatically while the
  // native dropdown is open.
  const selectStyle =
    'bg-white/5 border border-white/10 rounded-lg px-3 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20 max-w-[200px]';
  const hasActiveFilter =
    !!search.q ||
    !!search.quartal ||
    !!search.dateFrom ||
    !!search.dateTo ||
    !!search.supplierId ||
    !!search.mine ||
    (search.status?.length ?? 0) > 0 ||
    (search.categoryId?.length ?? 0) > 0 ||
    (search.source?.length ?? 0) > 0;

  return (
    <GlassPanel
      variant="subtle"
      padding="md"
      data-testid="expenses-filter-bar"
      className="sticky top-0 z-20 !bg-zinc-950/95"
    >
      {/* 006b P3: Kanon-Anatomie (PATTERNS.md §1) — Suche links (flex-1),
          Filter-Controls rechts; gleiche Zeile wie auf allen List-Pages. */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex-1 lg:min-w-[200px] lg:max-w-sm">
          <SearchInput
            value={search.q ?? ''}
            onChange={(q) => onChange({ q: q || undefined })}
            placeholder="Suche (Nr / Notiz)"
            data-testid="filter-search"
          />
        </div>
        <button type="button" aria-expanded={mobileExpanded} onClick={() => setMobileExpanded((open) => !open)} className="lg:hidden text-sm text-white/70 px-3 py-2 rounded-lg border border-white/10">
          {mobileExpanded ? 'Filter einklappen' : 'Filter und Datumsbasis'}
        </button>
        <div className={`${mobileExpanded ? 'flex' : 'hidden lg:flex'} flex-wrap items-center gap-2`}>
        {/* T565b — PeriodPicker replaces the prior quartal-<select> + dateFrom/dateTo
            inputs. Single Zeitraum-Button cycles current quartal / YYYY-Qn / Custom /
            Alle. URL-state stays compatible (`quartal=YYYY-Qn` per shared validation
            §131-155, dateFrom/dateTo for custom range). */}
        <DateBasisControls dateBasis={search.dateBasis} paymentStatus={search.paymentStatus}
          onBasisChange={(basis) => onChange({ dateBasis: basis, paymentStatus: 'all' })}
          onPaymentStatusChange={(status) => onChange({ paymentStatus: status, quartal: undefined, dateFrom: undefined, dateTo: undefined })} />
        <PeriodPicker
          value={{ quartal: search.quartal, dateFrom: search.dateFrom, dateTo: search.dateTo }}
          onChange={(next) =>
            onChange({
              paymentStatus: search.paymentStatus === 'unknown' && (next.quartal || next.dateFrom || next.dateTo) ? 'known' : search.paymentStatus,
              quartal: next.quartal,
              dateFrom: next.dateFrom,
              dateTo: next.dateTo,
            })
          }
        />

        {/* Status multi-select via a tiny checkbox cluster for accessibility */}
        <details className="relative">
          <summary
            className={selectStyle + ' list-none cursor-pointer flex items-center gap-2'}
            data-testid="filter-status-toggle"
          >
            Status {search.status?.length ? `(${search.status.length})` : ''}
          </summary>
          <div
            className="absolute mt-1 w-56 max-h-64 overflow-y-auto bg-zinc-900 border border-white/10 shadow-xl shadow-black/40 rounded-lg p-2 z-30"
            data-testid="filter-status-panel"
          >
            {STATUS_VALUES.map((s) => (
              <label key={s} className="flex items-center gap-2 px-2 py-1 text-sm text-white/80 hover:bg-white/5 rounded">
                <input
                  type="checkbox"
                  checked={search.status?.includes(s) ?? false}
                  onChange={(e) => {
                    const next = new Set(search.status ?? []);
                    if (e.target.checked) next.add(s);
                    else next.delete(s);
                    const arr = Array.from(next) as ExpenseStatusLiteral[];
                    onChange({ status: arr.length ? arr : undefined });
                  }}
                />
                {STATUS_LABELS[s] ?? s}
              </label>
            ))}
          </div>
        </details>

        {/* Source multi-select — same pattern */}
        <details className="relative">
          <summary
            className={selectStyle + ' list-none cursor-pointer flex items-center gap-2'}
            data-testid="filter-source-toggle"
          >
            Quelle {search.source?.length ? `(${search.source.length})` : ''}
          </summary>
          <div
            className="absolute mt-1 w-48 max-h-64 overflow-y-auto bg-zinc-900 border border-white/10 shadow-xl shadow-black/40 rounded-lg p-2 z-30"
            data-testid="filter-source-panel"
          >
            {SOURCE_VALUES.map((s) => (
              <label key={s} className="flex items-center gap-2 px-2 py-1 text-sm text-white/80 hover:bg-white/5 rounded">
                <input
                  type="checkbox"
                  checked={search.source?.includes(s) ?? false}
                  onChange={(e) => {
                    const next = new Set(search.source ?? []);
                    if (e.target.checked) next.add(s);
                    else next.delete(s);
                    const arr = Array.from(next) as ExpenseSourceLiteral[];
                    onChange({ source: arr.length ? arr : undefined });
                  }}
                />
                {SOURCE_LABELS[s]?.label ?? s}
              </label>
            ))}
          </div>
        </details>

        <select
          value={search.categoryId?.[0] ?? ''}
          onChange={(e) =>
            onChange({ categoryId: e.target.value ? [e.target.value] : undefined })
          }
          aria-label="Kategorie"
          data-testid="filter-category"
          className={selectStyle}
        >
          <option value="">Alle Kategorien</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          value={search.supplierId ?? ''}
          onChange={(e) => onChange({ supplierId: e.target.value || undefined })}
          aria-label="Lieferant"
          data-testid="filter-supplier"
          className={selectStyle}
        >
          <option value="">Alle Lieferanten</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </select>

        {/* T527 — "Nur meine Uploads" filter. T565 (FR-UI A1.2): rendered
            as a checkbox+label rather than a press-button; reads as a
            stateful predicate (\"on / off\"), not an action.  */}
        <label
          data-testid="filter-mine"
          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 cursor-pointer select-none text-sm"
        >
          <input
            type="checkbox"
            checked={!!search.mine}
            onChange={(e) =>
              onChange({ mine: e.target.checked ? true : undefined })
            }
            className="accent-sky-400"
          />
          <span>Nur meine Uploads</span>
        </label>

        {hasActiveFilter && (
          <Button
            variant="ghost"
            data-testid="filter-reset"
            onClick={() =>
              onChange({
                q: undefined,
                status: undefined,
                categoryId: undefined,
                supplierId: undefined,
                source: undefined,
                quartal: undefined,
                dateFrom: undefined,
                dateTo: undefined,
                mine: undefined,
              })
            }
          >
            ✕ Filter zurücksetzen
          </Button>
        )}
        </div>
      </div>
    </GlassPanel>
  );
}

function ExpensesInner() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  // T573: persisted page-size pref with URL-state override.
  const { pageSize, setPageSize } = usePageSizePref(
    'expense_list_page_size',
    search.limit
  );

  const { data, isLoading, error } = useExpenses({
    page: search.page,
    limit: pageSize,
    q: search.q,
    status: search.status,
    categoryId: search.categoryId,
    supplierId: search.supplierId,
    source: search.source,
    quartal: search.quartal,
    dateBasis: search.dateBasis,
    paymentStatus: search.paymentStatus,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    mine: search.mine,
  } as Parameters<typeof useExpenses>[0]);
  const { data: durcharbeite } = useDurcharbeiteQueue({ page: 1, limit: 1 });
  const { data: categoryData } = useCategories({});
  const { data: supplierData } = useSuppliers({});
  const duplicates = data ? detectDuplicatesInList(data.items) : new Set<string>();
  const reviewCount = durcharbeite?.pagination.total ?? 0;
  const ocrEngine = useOcrEngineState();
  // Spec 004 US1 (T009) — client-side closed-quartal lookup, reuses the
  // banner's TanStack Query cache; per-row Set.has() is O(1).
  const closedQuartals = useClosedQuartalsSet();

  // T-G4b-fe — bulk-merge selection state. Only ids from the currently
  // loaded page are eligible (cross-page merge is intentionally not
  // supported; users filter or paginate to the target candidates first).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState<string | null>(null);
  const mergeMutation = useMergeExpenses();
  const toast = useToastHelpers();
  function toggleRow(id: string, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  }
  function clearSelection() {
    setSelectedIds(new Set());
    setMergePrimaryId(null);
    setMergeModalOpen(false);
  }

  const updateSearch = (patch: Partial<ExpensesSearch>) => {
    navigate({
      search: (prev: ExpensesSearch) => ({ ...prev, ...patch, page: 1 }),
      replace: true,
    });
  };

  return (
    <div className="space-y-4">
      <ImapStatusBanner />
      <QuartalStatusBanner />
      <PageHeader
        title="Ausgaben"
        titleAccessory={<ExpenseStatusLegendTrigger />}
        subtitle={`Eingangs-Belege · ${data?.pagination.total ?? 0} Stück`}
        actions={
          <div className="flex gap-2 flex-wrap">
          {/* Spec 004 US2 (T013) FR-G2-DURCHARBEITE-PERSISTENT — render
              unconditionally so users don't lose the workflow when the
              queue drains to zero. Display rule:
                count > 0   → "⚠ Durcharbeiten (N)"  + amber tint
                count === 0 → "Durcharbeiten (0)"   + neutral tint
                count > 999 → "999+"                (toolbar overflow guard) */}
          <Link to="/expenses/durcharbeite">
            <Button
              variant="ghost"
              className={
                reviewCount > 0
                  ? 'text-amber-200 border-amber-500/30'
                  : 'text-white/50 border-white/10'
              }
              data-testid="expenses-durcharbeite-link"
              aria-label={
                reviewCount > 0
                  ? `Durcharbeiten – ${reviewCount} Belege in der Warteschlange`
                  : 'Durcharbeiten – Queue leer'
              }
            >
              {reviewCount > 0 ? '⚠ ' : ''}
              Durcharbeiten ({reviewCount > 999 ? '999+' : reviewCount})
            </Button>
          </Link>
          <Link to="/expenses/upload">
            <Button variant="ghost">↑ Multi-Upload</Button>
          </Link>
          <Link to="/expenses/new">
            <Button>+ Beleg erfassen</Button>
          </Link>
          </div>
        }
      />

      <ExpensesFilterBar
        search={search}
        onChange={updateSearch}
        categories={categoryData?.items ?? []}
        suppliers={supplierData?.items ?? []}
      />

      {/* T-G4b-fe — selection toolbar. Visible while any rows selected;
          Merge action only enabled at ≥2. Cross-page selection is not
          tracked: paginating resets state (selectedIds is in-route). */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between px-4 py-2 rounded-lg bg-sky-500/10 border border-sky-500/30"
          data-testid="expenses-bulk-toolbar"
        >
          <span className="text-sm text-sky-100">
            <span data-testid="expenses-bulk-selected-count">
              {selectedIds.size}
            </span>{' '}
            {selectedIds.size === 1 ? 'Beleg' : 'Belege'} ausgewählt
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              data-testid="expenses-bulk-clear"
            >
              Auswahl aufheben
            </Button>
            <Button
              size="sm"
              disabled={selectedIds.size < 2}
              onClick={() => {
                setMergePrimaryId(Array.from(selectedIds)[0] ?? null);
                setMergeModalOpen(true);
              }}
              data-testid="expenses-bulk-merge"
            >
              Zusammenführen…
            </Button>
          </div>
        </div>
      )}

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="text-red-400 bg-red-900/20 border border-red-800 rounded p-4">
          Fehler beim Laden: {(error as Error).message}
        </div>
      )}

      {data && data.items.length === 0 && (
        <EmptyState
          title="Keine Belege"
          description="Lege deinen ersten Beleg an — manuell oder per Upload."
          action={{
            label: 'Beleg erfassen',
            onClick: () => navigate({ to: '/expenses/new' }),
          }}
        />
      )}

      {data && data.items.length > 0 && (
        <GlassPanel
          variant="subtle"
          padding="none"
          className="overflow-hidden"
          data-testid="expenses-list-surface"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 [&_td]:align-middle">
            <thead>
              <tr className="text-left text-xs font-medium text-white/40 uppercase tracking-wider">
                {/* T-G4b-fe — bulk-select column. Header checkbox toggles
                    all currently-loaded rows; resets to indeterminate via
                    plain unchecked when partial selection is open. */}
                <th className="px-2 sm:px-4 py-3 w-8 hidden sm:table-cell" data-testid="th-select">
                  <input
                    type="checkbox"
                    aria-label="Alle Belege auf dieser Seite auswählen"
                    data-testid="expenses-select-all"
                    checked={
                      data.items.length > 0 &&
                      data.items.every((e) => selectedIds.has(e.id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const next = new Set(selectedIds);
                        for (const x of data.items) next.add(x.id);
                        setSelectedIds(next);
                      } else {
                        const next = new Set(selectedIds);
                        for (const x of data.items) next.delete(x.id);
                        setSelectedIds(next);
                      }
                    }}
                  />
                </th>
                <th className="px-2 sm:px-4 py-3" data-testid="th-datum">Datum</th>
                <th className="px-2 sm:px-4 py-3" data-testid="th-lieferant">Lieferant</th>
                <th className="px-2 sm:px-4 py-3 hidden sm:table-cell" data-testid="th-nr">Nr.</th>
                <th className="px-2 sm:px-4 py-3 hidden md:table-cell" data-testid="th-kategorie">Kategorie</th>
                <th className="px-2 sm:px-4 py-3 text-right hidden md:table-cell tabular-nums" data-testid="th-netto">Netto</th>
                <th className="px-2 sm:px-4 py-3 text-right hidden md:table-cell tabular-nums" data-testid="th-mwst">MwSt</th>
                <th className="px-2 sm:px-4 py-3 text-right tabular-nums" data-testid="th-brutto">Brutto</th>
                <th className="px-2 sm:px-4 py-3 hidden sm:table-cell" data-testid="th-quelle">Quelle</th>
                <th className="px-2 sm:px-4 py-3" data-testid="th-status">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.items.map((e) => {
                // Spec 004 US1 — derive 8-state row status. processingPhase
                // intentionally null (not projected on list — see DTO note
                // and `T-cdx-list-processing-phase` follow-up); the helper
                // degrades gracefully (ocr-failure tint won't fire on list).
                //
                // Codex Wave-A finding #2 (high) — `new Date(YYYY-MM-DD)`
                // parses as UTC midnight, so browsers west of UTC see the
                // PRIOR calendar day. That mis-routes the row to the wrong
                // quarter (e.g. 2026-04-01 → 2026-Q1 instead of Q2). Parse
                // the year + month directly from the ISO date string.
                const isoMatch = e.invoiceDate
                  ? /^(\d{4})-(\d{2})/.exec(e.invoiceDate)
                  : null;
                const quartalKey = isoMatch
                  ? `${isoMatch[1]}-Q${
                      Math.floor((Number(isoMatch[2]) - 1) / 3) + 1
                    }`
                  : null;
                const rowStatus = getExpenseRowStatus({
                  invoiceDate: e.invoiceDate,
                  dueDate: e.dueDate,
                  paymentDate: e.paymentDate,
                  supplierId: e.supplierId,
                  categoryId: e.categoryId,
                  grossTotal: e.grossTotal,
                  girocodeAmountMismatch: e.girocodeAmountMismatch ?? false,
                  erechnungAmountMismatch: e.erechnungAmountMismatch ?? null,
                  quartalClosed: quartalKey
                    ? closedQuartals.has(quartalKey)
                    : false,
                  processingPhase: null,
                  trustLevel: e.trustLevel,
                  source: e.source,
                  attachments: [],
                });
                const rowTint = ROW_TINT_CLASSES[rowStatus.primary];
                return (
                <tr
                  key={e.id}
                  onClick={() => navigate({ to: '/expenses/$id', params: { id: e.id } })}
                  className={`hover:bg-white/5 transition-colors cursor-pointer ${rowTint}`}
                  data-testid={`expense-row-${e.id}`}
                  data-row-status={rowStatus.primary}
                >
                  <td className="px-2 sm:px-4 py-3 w-8 hidden sm:table-cell">
                    <input
                      type="checkbox"
                      aria-label={`Beleg ${e.id.slice(0, 8)} auswählen`}
                      data-testid={`expense-select-${e.id}`}
                      checked={selectedIds.has(e.id)}
                      onChange={(ev) => toggleRow(e.id, ev.target.checked)}
                      // Stop row-click navigation when checkbox is clicked
                      onClick={(ev) => ev.stopPropagation()}
                    />
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-white/70">
                    <span className="inline-flex items-center gap-1.5">
                      {/* T-G1-fe (FR-G1) — invoice_date is nullable;
                          render NULL as "—" with a yellow-tinge marker
                          so it stays distinguishable from "Datum =
                          heute". Existing 📥 marker (T487 isInferredUploadDate)
                          is now a sibling case (older rows seeded with
                          today's date pre-T-G1-be still surface). */}
                      {e.invoiceDate ? (
                        <span data-testid={`expense-date-${e.id}`}>
                          {formatDateBerlin(e.invoiceDate)}
                        </span>
                      ) : (
                        <span
                          data-testid={`date-not-recognized-marker-${e.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-amber-500/15 border border-amber-500/40 text-amber-200"
                          aria-label="Belegdatum nicht erkannt"
                          title="Belegdatum konnte aus OCR nicht erkannt werden — bitte manuell setzen"
                        >
                          <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                          nicht erkannt
                        </span>
                      )}
                      {e.invoiceDate && isInferredUploadDate(e) && (
                        <span
                          title="Upload-Datum — Belegdatum noch nicht erkannt"
                          className="inline-flex items-center px-1 py-0.5 text-xs rounded bg-white/5 border border-white/10 text-white/50"
                          data-testid={`upload-date-marker-${e.id}`}
                          aria-label="Upload-Datum, Belegdatum noch nicht erkannt"
                        >
                          📥
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-white">
                    <div className="flex items-center gap-2 min-w-0 max-w-[8rem] sm:max-w-none">
                      {/* T492 — row-marker: red dot for review-tier rows
                          that aren't eRechnung-validated. Proxy for "OCR
                          uncertain, needs attention". Failed-pipeline
                          marker would need per-row processingStatus and
                          is deferred until polling → SSE (T482-followup). */}
                      {e.trustLevel === 'review' && e.source !== 'erechnung' && (
                        <span
                          className="inline-block w-2 h-2 rounded-full bg-amber-400"
                          title="OCR-Erkennung unsicher — bitte prüfen"
                          aria-label="OCR unsicher"
                          data-testid={`ocr-row-marker-${e.id}`}
                        />
                      )}
                      <Link
                        to="/expenses/$id"
                        params={{ id: e.id }}
                        className="hover:underline truncate"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        {e.supplierName ?? '—'}
                      </Link>
                      {duplicates.has(e.id) && (
                        <span
                          title="Ein weiterer Beleg mit gleichem Lieferant + Rechnungsnummer + Datum + Betrag ist im System"
                          className="inline-flex items-center px-1.5 py-0.5 text-xs rounded bg-amber-500/10 border border-amber-500/30 text-amber-200"
                          data-testid={`duplicate-badge-${e.id}`}
                          aria-label="Möglicherweise Duplikat"
                        >
                          ⚠ Duplikat?
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-white/50 font-mono text-sm hidden sm:table-cell">
                    {e.supplierInvoiceNumber ?? '—'}
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-white/70 hidden md:table-cell">{e.categoryName ?? '—'}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-white/70 tabular-nums hidden md:table-cell">
                    {formatMoney(e.netTotal, e.currency)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-right text-white/70 tabular-nums hidden md:table-cell">
                    {formatMoney(e.vatTotal, e.currency)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-right text-white tabular-nums">
                    {formatMoney(e.grossTotal, e.currency)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 hidden sm:table-cell">
                    {(() => {
                      const meta = SOURCE_LABELS[e.source] ?? { icon: '·', label: e.source };
                      return (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-white/5 border border-white/10 text-white/70"
                          data-testid={`source-${e.id}`}
                          title={`Quelle: ${meta.label}`}
                        >
                          <span aria-hidden="true">{meta.icon}</span>
                          <span>{meta.label}</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 sm:px-4 py-3">
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <StatusBadge tone={STATUS_TONES[e.status] ?? 'neutral'}>
                        {STATUS_LABELS[e.status] ?? e.status}
                      </StatusBadge>
                      {/* Spec 004 US1 (T009) — primary state tint + label
                          + secondary badges, computed per row above. */}
                      <ExpenseRowStatusBadge descriptor={rowStatus} />
                      {/* T477: per-row OCR badge so triage shows which
                          rows need attention. eRechnung-validated rows
                          stay clean; review rows surface as amber. */}
                      {ocrEngine !== 'production' && e.trustLevel === 'review' ? (
                        <OcrConfidenceBadge
                          kind="inactive"
                          label={ocrEngine === 'mocked' ? 'OCR gemockt' : 'OCR aus'}
                          testid={`row-ocr-${e.id}`}
                        />
                      ) : e.source !== 'erechnung' && e.trustLevel === 'review' ? (
                        <OcrConfidenceBadge
                          kind="review"
                          label="Review"
                          testid={`row-ocr-${e.id}`}
                        />
                      ) : null}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </GlassPanel>
      )}

      {/* T533 (FR-NFR-005) — pagination. Backend already returns
          PaginatedResponse; URL-state-`?page=N` plays nicely with the
          T489 filter bar (filter changes reset page=1 via updateSearch). */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:justify-center sm:items-center gap-3 mt-6">
          <Pagination
            page={search.page}
            totalPages={data.pagination.totalPages}
            onPageChange={(page) =>
              navigate({
                search: (prev: ExpensesSearch) => ({ ...prev, page }),
                replace: true,
              })
            }
          />
          {/* T573 — Belege pro Seite */}
          <PageSizePicker
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              navigate({
                search: (prev: ExpensesSearch) => ({ ...prev, limit: next, page: 1 }),
                replace: true,
              });
            }}
          />
        </div>
      )}

      {/* T-G4b-fe — merge confirm modal. Primary-picker = radio group
          over the selected expense IDs; confirm posts versions for every
          participant (optimistic-lock contract).
          006b P8: ad-hoc-Overlay → ui/Modal (Focus-Trap, ESC; Kanon §7). */}
      {mergeModalOpen && data && (
        <Modal
          isOpen
          onClose={() => setMergeModalOpen(false)}
          title="Belege zusammenführen"
          size="lg"
        >
          <div data-testid="expenses-merge-modal">
            <p className="text-white/50 text-sm mb-4">
              Wähle den Primär-Beleg. Anhänge der übrigen Belege werden
              dorthin übernommen; die zusammengeführten Belege bleiben in
              der DB mit <code>merged_into</code>-Pointer (Audit-Trail).
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.items
                .filter((e) => selectedIds.has(e.id))
                .map((e) => (
                  <label
                    key={e.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer"
                    data-testid={`expenses-merge-pick-${e.id}`}
                  >
                    <input
                      type="radio"
                      name="merge-primary"
                      value={e.id}
                      checked={mergePrimaryId === e.id}
                      onChange={() => setMergePrimaryId(e.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">
                        {e.supplierName ?? '—'}
                      </div>
                      <div className="text-white/50 text-xs flex gap-3">
                        <span>{e.supplierInvoiceNumber ?? '—'}</span>
                        <span>{e.invoiceDate ? formatDateBerlin(e.invoiceDate) : '—'}</span>
                        <span className="tabular-nums">
                          {formatMoney(e.grossTotal, e.currency)}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="ghost"
                onClick={() => setMergeModalOpen(false)}
                data-testid="expenses-merge-cancel"
              >
                Abbrechen
              </Button>
              <Button
                disabled={!mergePrimaryId || mergeMutation.isPending}
                data-testid="expenses-merge-confirm"
                onClick={async () => {
                  if (!mergePrimaryId) return;
                  const merged = Array.from(selectedIds).filter(
                    (id) => id !== mergePrimaryId,
                  );
                  const versions: Record<string, number> = {};
                  for (const item of data.items) {
                    if (selectedIds.has(item.id)) versions[item.id] = item.version;
                  }
                  try {
                    const res = await mergeMutation.mutateAsync({
                      primaryId: mergePrimaryId,
                      mergedIds: merged,
                      versions,
                    });
                    toast.success(
                      `Zusammengeführt: ${res.merged_count} Belege, ${res.ported_attachments} Anhänge`,
                    );
                    clearSelection();
                  } catch (err) {
                    toast.error(
                      `Zusammenführung fehlgeschlagen: ${(err as Error).message}`,
                    );
                  }
                }}
              >
                {mergeMutation.isPending ? 'Wird zusammengeführt…' : 'Zusammenführen'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
